// TODO: This file (430 lines) exceeds the 200-line guideline. Consider splitting into:
//   - cellSelectionState.ts (selection tracking, keyboard navigation)
//   - cellCopyBuilders.ts (buildCopyText, buildCopyTextAsync, buildCopyRowText, fetchFullBufferRows)
import { useState, useCallback, useRef, useEffect, type RefObject } from 'react';
import { escapeHtmlCell } from '../utils/clipboardFormatters';

type CellCoord = { rowIndex: number; columnId: string };

function cellKey(rowIndex: number, columnId: string): string {
  return `${rowIndex}:${columnId}`;
}

function parseCellKey(key: string): CellCoord {
  const sep = key.indexOf(':');
  return { rowIndex: Number(key.slice(0, sep)), columnId: key.slice(sep + 1) };
}

/**
 * Optional per-cell transform applied before a raw value is formatted for
 * copy/export (T5). Used to swap a native-spatial column's raw wire value
 * for its WKT text; every other column passes `raw` straight through.
 * Callers build this from `(await loadSpatialDecoder()).transformSpatialForExport`
 * once spatial columns are present — see ResultsGrid.tsx / TableExplorerGrid.tsx.
 */
export type CopyValueTransform = (raw: unknown, columnId: string) => unknown;

/** Build a Set of cell keys for the rectangle between two cells */
function buildRect(
  a: CellCoord,
  b: CellCoord,
  columnIds: string[]
): Set<string> {
  const r0 = Math.min(a.rowIndex, b.rowIndex);
  const r1 = Math.max(a.rowIndex, b.rowIndex);
  const ci0 = columnIds.indexOf(a.columnId);
  const ci1 = columnIds.indexOf(b.columnId);
  if (ci0 === -1 || ci1 === -1) return new Set();

  const c0 = Math.min(ci0, ci1);
  const c1 = Math.max(ci0, ci1);

  const cells = new Set<string>();
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      cells.add(cellKey(r, columnIds[c]));
    }
  }
  return cells;
}

/** `remapRows`' re-key of one cell set: the same Set when nothing moved, cells of rows that are gone dropped. */
function remapCellKeys(cells: Set<string>, mapRow: (rowIndex: number) => number | null): Set<string> {
  if (cells.size === 0) return cells;
  const next = new Set<string>();
  let changed = false;
  for (const key of cells) {
    const { rowIndex, columnId } = parseCellKey(key);
    const mapped = mapRow(rowIndex);
    if (mapped !== rowIndex) changed = true;
    if (mapped !== null) next.add(cellKey(mapped, columnId));
  }
  return changed ? next : cells;
}

function mapCells(cells: Set<string>, mapCell: (cell: CellCoord) => CellCoord | null): Set<string> {
  if (cells.size === 0) return cells;
  const next = new Set<string>();
  let changed = false;
  for (const key of cells) {
    const mapped = mapCell(parseCellKey(key));
    const nextKey = mapped ? cellKey(mapped.rowIndex, mapped.columnId) : null;
    if (nextKey !== key) changed = true;
    if (nextKey !== null) next.add(nextKey);
  }
  return changed ? next : cells;
}

/**
 * True for a key event from inside a dialog opened OVER the grid — one that does not contain
 * `gridRoot`: its Escape closes that dialog, not the grid selection (review E E3). A grid embedded
 * in a dialog owns an Escape pressed inside that dialog (review F G3). With no root known, every
 * dialog counts as an overlay.
 */
export function isDialogOriginEvent(event: Event, gridRoot?: Element | null): boolean {
  if (!(event.target instanceof Element)) return false;
  const dialog = event.target.closest('[role="dialog"], [role="alertdialog"]');
  return dialog !== null && !(gridRoot && dialog.contains(gridRoot));
}

type UseCellSelectionParams = {
  columnIds: string[];
  disabled: boolean;
  /** The grid's root element: an Escape from a dialog that contains it is the grid's own (see `isDialogOriginEvent`). */
  rootRef?: RefObject<Element | null>;
};

export type CellAggregateValues = {
  count: number;
  numericCount: number;
  sum: number | null;
  avg: number | null;
  min: number | null;
  max: number | null;
};

export function useCellSelection({
  columnIds,
  disabled,
  rootRef,
}: UseCellSelectionParams) {
  const [selectedCells, setSelectedCells] = useState<Set<string>>(
    () => new Set()
  );
  const anchorRef = useRef<CellCoord | null>(null);
  const isDraggingRef = useRef(false);
  // Cells selected before a drag started (for Ctrl+drag to add to existing)
  const preDragCellsRef = useRef<Set<string>>(new Set());
  const columnIdsRef = useRef(columnIds);
  columnIdsRef.current = columnIds;

  const clearSelection = useCallback(() => {
    setSelectedCells((prev) => (prev.size === 0 ? prev : new Set()));
    anchorRef.current = null;
    isDraggingRef.current = false;
  }, []);

  // Clear when disabled (edit mode toggled on)
  useEffect(() => {
    if (disabled) clearSelection();
  }, [disabled, clearSelection]);

  // Clear browser text selection when multi-cell selected (covers drag + shift+click)
  useEffect(() => {
    if (selectedCells.size > 1) {
      window.getSelection()?.removeAllRanges();
    }
  }, [selectedCells]);

  // Escape key clears selection — except an Escape pressed inside a dialog opened over the
  // grid: that one closes the dialog and must leave the grid's selection alone (review E E3).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDialogOriginEvent(e, rootRef?.current)) clearSelection();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [clearSelection, rootRef]);

  const handleCellMouseDown = useCallback(
    (rowIndex: number, columnId: string, e: React.MouseEvent) => {
      if (disabled) return;

      const coord: CellCoord = { rowIndex, columnId };
      const isMetaKey = e.metaKey || e.ctrlKey;
      const isShiftKey = e.shiftKey;
      // T30 — right-click WITHIN an existing multi-cell selection preserves it (standard
      // desktop convention: Explorer/Finder/Excel don't collapse a multi-selection just
      // because you right-clicked one of the already-selected items — only a right-click
      // OUTSIDE the selection resets it). Without this, a context-menu action gated on
      // `selectedCells.size > 1` (e.g. "Copy selection", "Compare geometries") could never
      // fire from a right-click, since the plain-click branch below would always collapse
      // the selection to the single clicked cell first.
      const preservesRightClickSelection =
        e.button === 2 &&
        !isMetaKey &&
        !isShiftKey &&
        selectedCells.size > 1 &&
        selectedCells.has(cellKey(rowIndex, columnId));

      if (isShiftKey && anchorRef.current) {
        // Prevent browser from extending native text selection on shift+click
        e.preventDefault();
        window.getSelection()?.removeAllRanges();
        // Shift+click: rectangular range from anchor to this cell
        const rect = buildRect(
          anchorRef.current,
          coord,
          columnIdsRef.current
        );
        setSelectedCells(rect);
      } else if (isMetaKey) {
        // Ctrl/Cmd+click: toggle this cell
        setSelectedCells((prev) => {
          const next = new Set(prev);
          const key = cellKey(rowIndex, columnId);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
        anchorRef.current = coord;
      } else if (preservesRightClickSelection) {
        anchorRef.current = coord;
      } else {
        // Plain click: start new selection
        const key = cellKey(rowIndex, columnId);
        setSelectedCells(new Set([key]));
        anchorRef.current = coord;
      }

      // Start drag tracking
      isDraggingRef.current = true;
      preDragCellsRef.current = isMetaKey
        ? new Set(selectedCells)
        : new Set();

      const onMouseUp = () => {
        isDraggingRef.current = false;
        document.removeEventListener('mouseup', onMouseUp);
      };
      document.addEventListener('mouseup', onMouseUp);
    },
    [disabled, selectedCells]
  );

  const handleCellMouseEnter = useCallback(
    (rowIndex: number, columnId: string) => {
      if (!isDraggingRef.current || disabled || !anchorRef.current) return;

      // Clear browser text selection during drag
      window.getSelection()?.removeAllRanges();

      const rect = buildRect(
        anchorRef.current,
        { rowIndex, columnId },
        columnIdsRef.current
      );
      // Merge with pre-drag cells (for Ctrl+drag)
      if (preDragCellsRef.current.size > 0) {
        for (const k of preDragCellsRef.current) rect.add(k);
      }
      setSelectedCells(rect);
    },
    [disabled]
  );

  const isCellSelected = useCallback(
    (rowIndex: number, columnId: string): boolean => {
      return selectedCells.has(cellKey(rowIndex, columnId));
    },
    [selectedCells]
  );

  /** Opt-in (only the storage browser calls it; the SQL grids never do): re-key the selection after the
   *  grid's rows moved. `mapRow` gives a row's new index, or `null` when that row is gone (its cells drop
   *  out). An unchanged mapping keeps the same Set, so memoised rows do not re-render. The cells held for a
   *  Ctrl+drag in progress are re-keyed the same way (review R1: left on the old indexes, a page landing
   *  mid-drag merged whatever item now sat at an old index into the selection); a drag whose anchor row
   *  is gone ends. */
  const remapRows = useCallback((mapRow: (rowIndex: number) => number | null) => {
    setSelectedCells((prev) => remapCellKeys(prev, mapRow));
    preDragCellsRef.current = remapCellKeys(preDragCellsRef.current, mapRow);
    const anchor = anchorRef.current;
    const mappedAnchor = anchor ? mapRow(anchor.rowIndex) : null;
    anchorRef.current = anchor && mappedAnchor !== null ? { ...anchor, rowIndex: mappedAnchor } : null;
    if (!anchorRef.current) isDraggingRef.current = false;
  }, []);

  /** Opt-in (only the storage browser calls it; the SQL grids never do): re-key or drop individual cells. `mapCell`
   *  gives a cell's new coordinate, or `null` to drop it — the storage grid's row toggle drops every cell of a row
   *  (fix-round-A review UXA-04) and moves the cells of a column that disappeared to Name (UXA-05). The cells held for
   *  a Ctrl+drag are mapped the same way; the anchor moves with its cell and stays put when its cell is dropped. An
   *  unchanged mapping keeps the same Set. */
  const remapCells = useCallback((mapCell: (cell: CellCoord) => CellCoord | null) => {
    setSelectedCells((prev) => mapCells(prev, mapCell));
    preDragCellsRef.current = mapCells(preDragCellsRef.current, mapCell);
    const anchor = anchorRef.current;
    if (anchor) anchorRef.current = mapCell(anchor) ?? anchor;
  }, []);

  return {
    selectedCells,
    isCellSelected,
    handleCellMouseDown,
    handleCellMouseEnter,
    clearSelection,
    remapRows,
    remapCells,
    selectionCount: selectedCells.size,
    isMultiCell: selectedCells.size > 1,
  };
}

/** Build an HTML <table> string from the same selected-cell rectangle as buildCopyText.
 *  Pasted into Teams / Outlook / Word, this renders as a formatted table. */
export function buildCopyHtml(
  selectedCells: Set<string>,
  rows: Record<string, unknown>[],
  columnIds: string[],
  colNames: Map<string, string>,
  colKeyMap: Map<string, string>,
  rowOffset = 0,
  valueTransform?: CopyValueTransform,
): string {
  // Single-cell selections paste as a raw value, not a 1-cell table — the
  // rich-clipboard helper falls back to writeText() when html is empty.
  if (selectedCells.size < 2) return '';

  const rowSet = new Set<number>();
  const colSet = new Set<number>();
  for (const cellId of selectedCells) {
    const { rowIndex, columnId } = parseCellKey(cellId);
    rowSet.add(rowIndex);
    const ci = columnIds.indexOf(columnId);
    if (ci !== -1) colSet.add(ci);
  }

  const sortedRows = [...rowSet].sort((a, b) => a - b);
  const sortedCols = [...colSet].sort((a, b) => a - b);

  const bodyRows: string[] = [];
  for (const ri of sortedRows) {
    const row = rows[ri - rowOffset];
    if (!row) continue;
    const cells: string[] = [];
    for (const ci of sortedCols) {
      const colId = columnIds[ci];
      const key = colKeyMap.get(colId) ?? colId;
      const raw = row[key];
      const val = valueTransform ? valueTransform(raw, colId) : raw;
      cells.push(`<td>${escapeHtmlCell(val)}</td>`);
    }
    bodyRows.push(`<tr>${cells.join('')}</tr>`);
  }

  if (bodyRows.length === 0) return '';

  const headerCells = sortedCols
    .map((ci) => `<th>${escapeHtmlCell(colNames.get(columnIds[ci]) ?? columnIds[ci])}</th>`)
    .join('');

  return `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows.join('')}</tbody></table>`;
}

/** Build tab-separated copy text from selected cells.
 *  Rows are newline-separated, columns are tab-separated. */
export function buildCopyText(
  selectedCells: Set<string>,
  rows: Record<string, unknown>[],
  columnIds: string[],
  colKeyMap: Map<string, string>,
  /** When rows array starts at an offset (e.g., windowed buffer), subtract this from absolute row indices */
  rowOffset = 0,
  valueTransform?: CopyValueTransform,
): string {
  const rowSet = new Set<number>();
  const colSet = new Set<number>();
  for (const cellId of selectedCells) {
    const { rowIndex, columnId } = parseCellKey(cellId);
    rowSet.add(rowIndex);
    const ci = columnIds.indexOf(columnId);
    if (ci !== -1) colSet.add(ci);
  }

  const sortedRows = [...rowSet].sort((a, b) => a - b);
  const sortedCols = [...colSet].sort((a, b) => a - b);

  const lines: string[] = [];
  for (const ri of sortedRows) {
    const row = rows[ri - rowOffset];
    if (!row) continue;
    const vals: string[] = [];
    for (const ci of sortedCols) {
      const colId = columnIds[ci];
      const key = colKeyMap.get(colId) ?? colId;
      const raw = valueTransform ? valueTransform(row[key], colId) : row[key];
      if (raw === null || raw === undefined) vals.push('');
      else if (typeof raw === 'object') vals.push(JSON.stringify(raw));
      else vals.push(String(raw));
    }
    lines.push(vals.join('\t'));
  }
  return lines.join('\n');
}

/** Format a raw value for clipboard text */
function formatCellValue(raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'object') return JSON.stringify(raw);
  return String(raw);
}

/** Max cells before we skip full-value fetch and use preview values instead */
const FULL_VALUE_CELL_LIMIT = 10_000;

type CellValueResult = { value: unknown; generation: number };
type GetCellValueFn = (rowIndex: number, columnId: string) => Promise<CellValueResult>;

/**
 * Async version of buildCopyText that fetches full (untruncated) cell values
 * from the backend via getCellValue IPC in windowed mode.
 *
 * Returns `{ text, usedPreview, staleCount }` where `usedPreview` is true when the
 * performance guard kicked in (>10K cells) or getCellValue was not provided.
 * `staleCount` is the number of cells that returned a stale generation and fell back to preview.
 */
export async function buildCopyTextAsync(
  selectedCells: Set<string>,
  rows: Record<string, unknown>[],
  columnIds: string[],
  colKeyMap: Map<string, string>,
  rowOffset: number,
  getCellValue?: GetCellValueFn,
  sourceGeneration?: number,
  colNames?: Map<string, string>,
  valueTransform?: CopyValueTransform,
): Promise<{ text: string; html: string; usedPreview: boolean; staleCount: number }> {
  if (selectedCells.size === 0) return { text: '', html: '', usedPreview: false, staleCount: 0 };

  // Performance guard: skip full-value fetch for huge selections
  if (!getCellValue || selectedCells.size > FULL_VALUE_CELL_LIMIT) {
    return {
      text: buildCopyText(selectedCells, rows, columnIds, colKeyMap, rowOffset, valueTransform),
      html: buildCopyHtml(selectedCells, rows, columnIds, colNames ?? colKeyMap, colKeyMap, rowOffset, valueTransform),
      usedPreview: !!getCellValue && selectedCells.size > FULL_VALUE_CELL_LIMIT,
      staleCount: 0,
    };
  }

  const rowSet = new Set<number>();
  const colSet = new Set<number>();
  for (const cellId of selectedCells) {
    const { rowIndex, columnId } = parseCellKey(cellId);
    rowSet.add(rowIndex);
    const ci = columnIds.indexOf(columnId);
    if (ci !== -1) colSet.add(ci);
  }

  const sortedRows = [...rowSet].sort((a, b) => a - b);
  const sortedCols = [...colSet].sort((a, b) => a - b);

  // Filter to rows in buffer and fetch full values
  const inBufferRows = sortedRows.filter((ri) => {
    const bufIdx = ri - rowOffset;
    return bufIdx >= 0 && bufIdx < rows.length;
  });

  // Batch-fetch full values via Promise.all
  type FetchEntry = { ri: number; ci: number; promise: Promise<CellValueResult | 'fallback'> };
  const fetchPromises: FetchEntry[] = [];
  for (const ri of inBufferRows) {
    for (const ci of sortedCols) {
      const colId = columnIds[ci];
      if (selectedCells.has(cellKey(ri, colId))) {
        fetchPromises.push({ ri, ci, promise: getCellValue(ri, colId).catch((): 'fallback' => 'fallback') });
      }
    }
  }

  const resolved = await Promise.all(fetchPromises.map((f) => f.promise));
  const valueMap = new Map<string, unknown>();
  let staleCount = 0;
  fetchPromises.forEach((f, i) => {
    const result = resolved[i];
    const key = cellKey(f.ri, columnIds[f.ci]);
    if (result === 'fallback') {
      const row = rows[f.ri - rowOffset];
      const colKey = colKeyMap.get(columnIds[f.ci]) ?? columnIds[f.ci];
      valueMap.set(key, row?.[colKey]);
    } else if (sourceGeneration !== undefined && result.generation < sourceGeneration) {
      staleCount++;
      const row = rows[f.ri - rowOffset];
      const colKey = colKeyMap.get(columnIds[f.ci]) ?? columnIds[f.ci];
      valueMap.set(key, row?.[colKey]);
    } else {
      valueMap.set(key, result.value);
    }
  });

  const lines: string[] = [];
  const htmlRows: string[] = [];
  for (const ri of inBufferRows) {
    const vals: string[] = [];
    const htmlCells: string[] = [];
    for (const ci of sortedCols) {
      const colId = columnIds[ci];
      if (!selectedCells.has(cellKey(ri, colId))) continue;
      const rawFetched = valueMap.get(cellKey(ri, colId));
      const raw = valueTransform ? valueTransform(rawFetched, colId) : rawFetched;
      vals.push(formatCellValue(raw));
      htmlCells.push(`<td>${escapeHtmlCell(raw)}</td>`);
    }
    if (vals.length > 0) {
      lines.push(vals.join('\t'));
      htmlRows.push(`<tr>${htmlCells.join('')}</tr>`);
    }
  }

  let html = '';
  // Single-cell selection: leave html empty so writeRichToClipboard falls back
  // to writeText (raw cell value, not a 1-cell table).
  if (htmlRows.length > 0 && selectedCells.size >= 2) {
    const headerCells = sortedCols
      .map((ci) => {
        const colId = columnIds[ci];
        const label = colNames?.get(colId) ?? colKeyMap.get(colId) ?? colId;
        return `<th>${escapeHtmlCell(label)}</th>`;
      })
      .join('');
    html = `<table><thead><tr>${headerCells}</tr></thead><tbody>${htmlRows.join('')}</tbody></table>`;
  }

  return { text: lines.join('\n'), html, usedPreview: false, staleCount };
}

/**
 * Build tab-separated text for a single row with header.
 * Format: "col1\tcol2\tcol3\nval1\tval2\tval3"
 */
export async function buildCopyRowText(
  rowIndex: number,
  rows: Record<string, unknown>[],
  columns: Array<{ id: string; name: string; key: string }>,
  rowOffset: number,
  getCellValue?: GetCellValueFn,
  sourceGeneration?: number,
  valueTransform?: CopyValueTransform,
): Promise<string> {
  const bufIdx = rowIndex - rowOffset;
  const row = rows[bufIdx];
  if (!row) return '';

  const header = columns.map((c) => c.name).join('\t');
  const applyTransform = (raw: unknown, columnId: string): unknown =>
    valueTransform ? valueTransform(raw, columnId) : raw;

  let vals: string[];
  if (getCellValue) {
    const results = await Promise.all(
      columns.map((c) =>
        getCellValue(rowIndex, c.id).catch((): null => null)
      )
    );
    vals = results.map((result, i) => {
      if (result === null) return formatCellValue(applyTransform(row[columns[i].key], columns[i].id));
      if (sourceGeneration !== undefined && result.generation < sourceGeneration) {
        return formatCellValue(applyTransform(row[columns[i].key], columns[i].id));
      }
      return formatCellValue(applyTransform(result.value, columns[i].id));
    });
  } else {
    vals = columns.map((c) => formatCellValue(applyTransform(row[c.key], c.id)));
  }

  return `${header}\n${vals.join('\t')}`;
}

/**
 * Fetch full (untruncated) cell values for all rows in the buffer.
 * Returns new row objects with full values replacing the 1KB previews.
 * Skips fetch if cell count exceeds the threshold — returns null to signal
 * the caller should use preview values with disclosure.
 */
export async function fetchFullBufferRows(
  rows: Record<string, unknown>[],
  columns: Array<{ id?: string; name: string; key?: string }>,
  rowOffset: number,
  getCellValue: GetCellValueFn,
  sourceGeneration?: number,
): Promise<{ rows: Record<string, unknown>[]; usedPreview: boolean }> {
  const cellCount = rows.length * columns.length;
  if (cellCount > FULL_VALUE_CELL_LIMIT) {
    return { rows, usedPreview: true };
  }
  if (rows.length === 0) {
    return { rows, usedPreview: false };
  }

  // Batch-fetch all cell values
  type FetchEntry = { bufIdx: number; colId: string; colKey: string; promise: Promise<CellValueResult | 'fallback'> };
  const fetches: FetchEntry[] = [];
  for (let bufIdx = 0; bufIdx < rows.length; bufIdx++) {
    const rowIndex = bufIdx + rowOffset;
    for (const col of columns) {
      const colId = col.id ?? col.name;
      const colKey = col.key ?? colId;
      fetches.push({
        bufIdx, colId, colKey,
        promise: getCellValue(rowIndex, colId).catch((): 'fallback' => 'fallback'),
      });
    }
  }

  const results = await Promise.all(fetches.map((f) => f.promise));
  const fullRows = rows.map((row) => ({ ...row }));
  fetches.forEach((f, i) => {
    const result = results[i];
    if (result === 'fallback') return; // keep preview value
    if (sourceGeneration !== undefined && result.generation < sourceGeneration) return; // stale — keep preview
    fullRows[f.bufIdx][f.colKey] = result.value;
  });

  return { rows: fullRows, usedPreview: false };
}

/** Check if any selected cells are outside the buffer range */
export function hasOutOfBufferCells(
  selectedCells: Set<string>,
  rowOffset: number,
  bufferLength: number,
): boolean {
  for (const cellId of selectedCells) {
    const { rowIndex } = parseCellKey(cellId);
    const bufIdx = rowIndex - rowOffset;
    if (bufIdx < 0 || bufIdx >= bufferLength) return true;
  }
  return false;
}

export { parseCellKey };
